/**
 * BB token economy — pricing config + pack/subscription catalogue.
 *
 *   • All AI-generation actions (video, image, audio, advisor calls) cost tokens.
 *   • Normal Film Maker editing (color grade, trimming, mixing, lyric-sync engine,
 *     local timeline operations) is and stays FREE.
 *   • Tokens are bought one-time in packs OR refilled monthly via subscription.
 *
 * 100 tokens = $1.00 USD baseline. Costs include ~25 % BB margin to cover infra,
 * retries, support and platform sustainability. Bigger packs include bonus
 * tokens.
 */

import type { VideoProvider } from '@/lib/video-generators';

export const USD_PER_TOKEN = 0.01; // 100 tokens = $1

/**
 * What BB actually pays providers, per second of generated video — Gordon:
 * "how much more expensive are we than the tokens we buy? Show it, be
 * transparent." Verified against each provider's own current published
 * rate (not a live/fluctuating spot price — none of these expose one; this
 * is their fixed list price, checked against docs on 2026-08-17):
 *   veo      — ai.google.dev/gemini-api/docs/veo: Lite tier $0.08/s at
 *              1080p with audio included (the resolution/tier BB actually
 *              requests). The old "~$0.30/s" figure here was stale/wrong —
 *              likely inherited from Veo's Standard tier, not Lite.
 *   kling    — fal.ai's own listed rate for Kling 3.0: $0.084/s.
 *   minimax  — fal.ai's listed rate for Hailuo 02 [Standard] (768p, the
 *              tier BB requests): $0.045/s.
 *   seedance, vidu, wan, hunyuan — NOT independently re-verified this pass;
 *   carried over from the prior estimate, flagged accordingly below.
 */
export const UNDERLYING_USD_PER_SEC: Record<VideoProvider, { cost: number; verified: boolean }> = {
  veo:      { cost: 0.08,  verified: true },
  kling:    { cost: 0.084, verified: true },
  minimax:  { cost: 0.045, verified: true },
  seedance: { cost: 0.24,  verified: false },
  vidu:     { cost: 0.04,  verified: false },
  wan:      { cost: 0.03,  verified: false },
  hunyuan:  { cost: 0.03,  verified: false },
};

/** Per-second token cost by video provider (with BB margin baked in). */
export const VIDEO_TOKEN_RATE: Record<VideoProvider, number> = {
  veo:      10, //  $0.08/s underlying × ~1.25 margin → $0.10/s → 10 tokens
  seedance: 30, //  ~$0.24/s (unverified)
  kling:    11, //  $0.084/s underlying × ~1.25 margin → 11 tokens
  minimax:  6,  //  $0.045/s underlying × ~1.25 margin → 6 tokens
  vidu:     5,
  wan:      4,
  hunyuan:  4,
};

/**
 * Full margin breakdown for a video generation — the transparency view
 * shown right in AI Scene where the spend decision actually happens, not
 * just buried in Super Admin. `bonusMarginPct` is an extra multiplier
 * layered on top of the standard rate for comparison — Gordon asked for it
 * literally as "π%".
 */
export function videoMarginBreakdown(provider: VideoProvider, durationSec: number, bonusMarginPct = Math.PI) {
  const dur = Math.max(2, Math.min(15, durationSec || 8));
  const { cost: underlyingPerSec, verified } = UNDERLYING_USD_PER_SEC[provider];
  const underlyingUsd = underlyingPerSec * dur;
  const chargedTokens = videoTokenCost(provider, dur);
  const chargedUsd = chargedTokens * USD_PER_TOKEN;
  const marginPct = underlyingUsd > 0 ? ((chargedUsd - underlyingUsd) / chargedUsd) * 100 : 0;
  const withBonusUsd = chargedUsd * (1 + bonusMarginPct / 100);
  return { underlyingUsd, chargedUsd, chargedTokens, marginPct, withBonusUsd, verified };
}

/** Per-call token cost for non-video AI actions. */
export const AI_ACTION_COST = {
  artworkGeneration:   50,   // ~one image
  artworkEnhance:      20,
  lyricsGenerate:      25,
  creditsExtract:      15,
  creditsParseText:    10,
  legalGuardianQuery:  30,
  creatorAdvisorQuery: 20,
  trackAnalyse:        15,
  videoAnalyse:        25,   // for the future Kimi-style video understanding
  ttsMinute:           40,   // per minute of TTS
  transcribeMinute:    15,   // per minute of transcription
} as const;

export type AiActionKey = keyof typeof AI_ACTION_COST;

/** One-time token packs (each USD value maps to a Stripe price id at runtime). */
export interface TokenPack {
  id: string;
  label: string;
  tokens: number;
  bonus: number;
  priceUsd: number;       // total charged
  highlight?: boolean;
  description: string;
}

export const TOKEN_PACKS: TokenPack[] = [
  {
    id: 'starter',
    label: 'Starter',
    tokens: 500, bonus: 0, priceUsd: 5,
    description: '~13s of Veo · ~30s of Kling · ~10 artwork generations',
  },
  {
    id: 'plus',
    label: 'Plus',
    tokens: 2500, bonus: 500, priceUsd: 20,
    description: '3,000 total · 20% bonus · ~78s of Veo',
    highlight: true,
  },
  {
    id: 'pro',
    label: 'Pro',
    tokens: 7500, bonus: 2500, priceUsd: 50,
    description: '10,000 total · 33% bonus · best $/token',
  },
  {
    id: 'studio',
    label: 'Studio',
    tokens: 16000, bonus: 6000, priceUsd: 100,
    description: '22,000 total · 38% bonus · for heavy creators',
  },
];

/** Monthly subscriptions — tokens auto-grant on each renewal. */
export interface TokenSubscription {
  id: string;
  label: string;
  monthlyTokens: number;
  priceUsd: number;
  description: string;
}

export const TOKEN_SUBSCRIPTIONS: TokenSubscription[] = [
  {
    id: 'sub_starter',
    label: 'Creator',
    monthlyTokens: 1000,
    priceUsd: 9,
    description: 'Cancel anytime. Unused tokens roll over.',
  },
  {
    id: 'sub_pro',
    label: 'Pro',
    monthlyTokens: 3500,
    priceUsd: 29,
    description: 'Best for weekly releases / lyric videos.',
  },
  {
    id: 'sub_studio',
    label: 'Studio',
    monthlyTokens: 10000,
    priceUsd: 79,
    description: 'Heavy AI use: music videos, daily clips.',
  },
];

/** Compute token cost for a video generation call. */
export function videoTokenCost(provider: VideoProvider, durationSec: number): number {
  const rate = VIDEO_TOKEN_RATE[provider] ?? 30;
  return Math.ceil(rate * Math.max(2, Math.min(15, durationSec || 8)));
}

/** Friendly USD display of a token amount. */
export function tokensToUsd(tokens: number): string {
  return `$${(tokens * USD_PER_TOKEN).toFixed(2)}`;
}
